import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Lock,
  Play,
  Plus,
  Send,
  Square,
  Trash2,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useDecideTimesheet,
  useDeleteTimeEntry,
  usePendingApprovals,
  useProjects,
  useRunningTimer,
  useSaveTimeEntry,
  useStartTimer,
  useStopTimer,
  useSubmitTimesheet,
  useTimesheetWeek,
  useUtilization,
} from './api';
import {
  ACTIVITY_KIND_LABELS,
  TIMESHEET_STATUS_LABELS,
  TIMESHEET_STATUS_VARIANT,
  formatMinutes,
  parseHoursInput,
} from './labels';
import type { PendingApproval } from './types';

const TABS = [
  ['week', 'My week'],
  ['approvals', 'Approvals'],
  ['utilization', 'Utilisation'],
] as const;

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function TimesheetsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'week';

  return (
    <div>
      <PageHeader
        title="Timesheets"
        description="Time is kept in minutes and locked once approved — that lock is what lets invoicing trust it."
        actions={<TimerWidget />}
      />

      <div className="mb-5 flex gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'week' && <MyWeek />}
      {tab === 'approvals' && <Approvals />}
      {tab === 'utilization' && <UtilizationReport />}
    </div>
  );
}

/** The running timer, always visible so nobody forgets one is going. */
function TimerWidget() {
  const { data: running } = useRunningTimer();
  const stop = useStopTimer();

  if (!running) return null;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      <div className="min-w-0 text-sm">
        <div className="tabular font-semibold">{formatMinutes(running.elapsedMinutes)}</div>
        <div className="truncate text-xs text-muted-foreground">
          {running.workItem?.key ?? running.ticket?.number ?? running.project?.name ?? 'Running'}
        </div>
      </div>
      <Button size="sm" variant="outline" disabled={stop.isPending} onClick={() => stop.mutate({})}>
        <Square className="h-3.5 w-3.5" />
        Stop
      </Button>
    </div>
  );
}

function MyWeek() {
  const [weekStart, setWeekStart] = useState<string | undefined>(undefined);
  const { data, isLoading, refetch } = useTimesheetWeek(undefined, weekStart);
  const submit = useSubmitTimesheet();
  const remove = useDeleteTimeEntry();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return <TableSkeleton columns={9} />;
  if (!data) return null;

  const shiftWeek = (days: number) => {
    const base = new Date(data.weekStart);
    base.setUTCDate(base.getUTCDate() + days);
    setWeekStart(base.toISOString().slice(0, 10));
  };

  const status = data.timesheet?.status ?? 'DRAFT';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => shiftWeek(-7)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">
          Week of {formatDate(data.weekStart)}
        </span>
        <Button size="sm" variant="outline" onClick={() => shiftWeek(7)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setWeekStart(undefined)}>
          This week
        </Button>

        <Badge variant={TIMESHEET_STATUS_VARIANT[status]} className="ms-2">
          {TIMESHEET_STATUS_LABELS[status]}
        </Badge>

        <div className="ms-auto flex items-center gap-2">
          <span className="tabular text-sm text-muted-foreground">
            {data.totalHours}h logged · {data.billableHours}h billable
          </span>
          {!data.locked && (
            <>
              <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
                <Plus className="h-4 w-4" />
                Add time
              </Button>
              <Button
                size="sm"
                disabled={submit.isPending || data.rows.length === 0}
                onClick={async () => {
                  setError(null);
                  try {
                    await submit.mutateAsync({ weekStart: data.weekStart });
                  } catch (caught) {
                    setError(
                      caught instanceof ApiRequestError ? caught.message : 'Could not submit.',
                    );
                  }
                }}
              >
                <Send className="h-4 w-4" />
                Submit week
              </Button>
            </>
          )}
        </div>
      </div>

      {data.timesheet?.rejectionReason && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <span className="font-medium text-destructive">Sent back: </span>
          {data.timesheet.rejectionReason}
        </div>
      )}

      {data.locked && (
        <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" />
          This week is {status === 'APPROVED' ? 'approved' : 'submitted'} and cannot be edited. Ask
          your manager to send it back if something is wrong.
        </div>
      )}

      <FormError message={error} />

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[14rem]">Project / activity</TableHead>
                {data.days.map((day, index) => (
                  <TableHead key={day} className="text-center">
                    <div>{DAY_NAMES[index]}</div>
                    <div className="text-[10px] font-normal text-muted-foreground">
                      {day.slice(8)}
                    </div>
                  </TableHead>
                ))}
                <TableHead className="text-end">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">
                    Nothing logged this week.
                  </TableCell>
                </TableRow>
              ) : (
                data.rows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>
                      <div className="text-sm font-medium">{row.projectName ?? 'No project'}</div>
                      <div className="text-xs text-muted-foreground">
                        {ACTIVITY_KIND_LABELS[row.activityKind]}
                        {!row.billable && ' · non-billable'}
                      </div>
                    </TableCell>
                    {data.days.map((day) => (
                      <TableCell key={day} className="tabular text-center text-sm">
                        {row.days[day] ? formatMinutes(row.days[day].minutes) : '—'}
                      </TableCell>
                    ))}
                    <TableCell className="tabular text-end text-sm font-semibold">
                      {row.totalHours}h
                    </TableCell>
                  </TableRow>
                ))
              )}
              <TableRow className="border-t-2 hover:bg-transparent">
                <TableCell className="font-semibold">Daily total</TableCell>
                {data.days.map((day) => (
                  <TableCell key={day} className="tabular text-center text-sm font-semibold">
                    {data.dayTotals[day] ? `${data.dayTotals[day]}h` : '—'}
                  </TableCell>
                ))}
                <TableCell className="tabular text-end font-bold">{data.totalHours}h</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data.entries.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Every entry</CardTitle>
          </CardHeader>
          <CardContent className="divide-y p-0">
            {data.entries.map((entry) => (
              <div key={entry.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="w-20 shrink-0 text-xs text-muted-foreground">
                  {formatDate(entry.date)}
                </span>
                <span className="tabular w-16 shrink-0 font-medium">
                  {formatMinutes(entry.minutes)}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {entry.workItem ? (
                    <span className="font-mono text-xs text-muted-foreground">
                      {entry.workItem.key}{' '}
                    </span>
                  ) : entry.ticket ? (
                    <span className="font-mono text-xs text-muted-foreground">
                      {entry.ticket.number}{' '}
                    </span>
                  ) : null}
                  {entry.description ?? entry.project?.name ?? '—'}
                </span>
                {!entry.billable && (
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    non-billable
                  </Badge>
                )}
                {entry.invoiceId && (
                  <Badge variant="success" className="shrink-0 text-[10px]">
                    invoiced
                  </Badge>
                )}
                {entry.hourBankId && (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">
                    from hour bank
                  </Badge>
                )}
                {!data.locked && !entry.invoiceId && (
                  <button
                    className="shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => remove.mutate(entry.id)}
                    aria-label="Delete entry"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {adding && (
        <AddTimeDialog
          onClose={() => {
            setAdding(false);
            void refetch();
          }}
        />
      )}
    </div>
  );
}

function AddTimeDialog({ onClose }: { onClose: () => void }) {
  const save = useSaveTimeEntry();
  const startTimer = useStartTimer();
  const { data: projects } = useProjects({ limit: 100 });
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    hours: '',
    projectId: '',
    activityKind: 'DEVELOPMENT',
    description: '',
    billable: true,
  });
  const [error, setError] = useState<string | null>(null);

  const minutes = parseHoursInput(form.hours);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log time</DialogTitle>
          <DialogDescription>
            Enter it as hours (1.5), as a clock reading (1:30), or as words (1h 30m).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(event) => setForm({ ...form, date: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>How long</Label>
              <Input
                placeholder="1.5"
                value={form.hours}
                onChange={(event) => setForm({ ...form, hours: event.target.value })}
              />
              {form.hours && minutes === null && (
                <p className="text-xs text-destructive">That is not a length of time.</p>
              )}
              {minutes !== null && (
                <p className="text-xs text-muted-foreground">{formatMinutes(minutes)}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select
                value={form.projectId}
                onChange={(event) => setForm({ ...form, projectId: event.target.value })}
              >
                <option value="">No project</option>
                {(projects?.data ?? []).map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.code} — {project.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Activity</Label>
              <Select
                value={form.activityKind}
                onChange={(event) => setForm({ ...form, activityKind: event.target.value })}
              >
                {Object.entries(ACTIVITY_KIND_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>What you did</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() =>
              startTimer.mutate(
                {
                  projectId: form.projectId || undefined,
                  activityKind: form.activityKind,
                  description: form.description || undefined,
                },
                { onSuccess: onClose },
              )
            }
          >
            <Play className="h-4 w-4" />
            Start a timer instead
          </Button>
          <Button
            disabled={save.isPending || minutes === null}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync({
                  date: form.date,
                  minutes,
                  projectId: form.projectId || undefined,
                  activityKind: form.activityKind,
                  description: form.description || undefined,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not log the time.',
                );
              }
            }}
          >
            <Clock className="h-4 w-4" />
            Log it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Approvals() {
  const { data: sheets = [], isLoading, error } = usePendingApprovals();
  const [rejecting, setRejecting] = useState<PendingApproval | null>(null);
  const decide = useDecideTimesheet();

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          You do not have permission to approve timesheets.
        </CardContent>
      </Card>
    );
  }

  if (isLoading) return <TableSkeleton columns={4} />;

  if (sheets.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Check className="mx-auto mb-3 h-8 w-8 text-emerald-500" />
          <p className="text-sm text-muted-foreground">Nothing waiting for you.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {sheets.map((sheet) => (
        <Card key={sheet.id}>
          <CardContent className="flex flex-wrap items-center gap-4 p-4">
            <div className="min-w-0 flex-1">
              <div className="font-medium">{sheet.user.name}</div>
              <div className="text-xs text-muted-foreground">
                Week of {formatDate(sheet.weekStart)}
                {sheet.projects.length > 0 && ` · ${sheet.projects.join(', ')}`}
              </div>
            </div>
            <div className="text-end">
              <div className="tabular font-semibold">{sheet.totalHours}h</div>
              <div className="text-xs text-muted-foreground">{sheet.billableHours}h billable</div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: sheet.id, approve: true })}
              >
                <Check className="h-4 w-4" />
                Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRejecting(sheet)}>
                Send back
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {rejecting && (
        <RejectDialog sheet={rejecting} onClose={() => setRejecting(null)} />
      )}
    </div>
  );
}

function RejectDialog({ sheet, onClose }: { sheet: PendingApproval; onClose: () => void }) {
  const decide = useDecideTimesheet();
  const [reason, setReason] = useState('');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send back {sheet.user.name}&rsquo;s timesheet</DialogTitle>
          <DialogDescription>
            They will see this reason, so make it something they can act on.
          </DialogDescription>
        </DialogHeader>

        <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={decide.isPending || reason.trim().length < 3}
            onClick={async () => {
              await decide.mutateAsync({ id: sheet.id, approve: false, reason });
              onClose();
            }}
          >
            Send back
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UtilizationReport() {
  const now = new Date();
  const [from, setFrom] = useState(
    new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
  );
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const [approvedOnly, setApprovedOnly] = useState(false);

  const { data, isLoading } = useUtilization({ from, to, approvedOnly });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input
            type="date"
            className="h-8"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input
            type="date"
            className="h-8"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <Button
          size="sm"
          variant={approvedOnly ? 'secondary' : 'outline'}
          onClick={() => setApprovedOnly((value) => !value)}
        >
          Approved time only
        </Button>
      </div>

      {isLoading || !data ? (
        <TableSkeleton columns={6} />
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Utilisation</CardTitle>
            <CardDescription>
              Billable hours over available hours. Leave and public holidays come out of available
              hours rather than counting as idle — a week off should not read as a lazy week.
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead className="text-end">Capacity</TableHead>
                  <TableHead className="text-end">Away</TableHead>
                  <TableHead className="text-end">Available</TableHead>
                  <TableHead className="text-end">Logged</TableHead>
                  <TableHead className="text-end">Billable</TableHead>
                  <TableHead className="text-end">Utilisation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row) => (
                  <TableRow key={row.userId}>
                    <TableCell>
                      <div className="font-medium">{row.name}</div>
                      {row.designation && (
                        <div className="text-xs text-muted-foreground">{row.designation}</div>
                      )}
                    </TableCell>
                    <TableCell className="tabular text-end">{row.capacityHours}h</TableCell>
                    <TableCell className="tabular text-end text-muted-foreground">
                      {row.awayHours ? `${row.awayHours}h` : '—'}
                    </TableCell>
                    <TableCell className="tabular text-end">{row.availableHours}h</TableCell>
                    <TableCell className="tabular text-end">{row.loggedHours}h</TableCell>
                    <TableCell className="tabular text-end font-medium">
                      {row.billableHours}h
                    </TableCell>
                    <TableCell
                      className={cn(
                        'tabular text-end font-semibold',
                        row.utilizationPercent !== null &&
                          (row.utilizationPercent >= 80
                            ? 'text-emerald-600'
                            : row.utilizationPercent < 50
                              ? 'text-amber-600'
                              : ''),
                      )}
                    >
                      {row.utilizationPercent === null ? '—' : `${row.utilizationPercent}%`}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 hover:bg-transparent">
                  <TableCell className="font-semibold">Team</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="tabular text-end font-semibold">
                    {data.totals.availableHours}h
                  </TableCell>
                  <TableCell className="tabular text-end font-semibold">
                    {data.totals.loggedHours}h
                  </TableCell>
                  <TableCell className="tabular text-end font-semibold">
                    {data.totals.billableHours}h
                  </TableCell>
                  <TableCell className="tabular text-end font-bold">
                    {data.totals.utilizationPercent === null
                      ? '—'
                      : `${data.totals.utilizationPercent}%`}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
